import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type GuardedResult<T> = { stale: false; value: T } | { stale: true }

export interface StaleGuard {
  /** Capture the current generation before awaiting. */
  ticket: () => number
  /** True while nothing has invalidated the generation since `ticket` was taken. */
  isCurrent: (ticket: number) => boolean
  /** Advance the generation: every in-flight result becomes stale. */
  invalidate: () => void
  /** Await a task and report whether its result is still wanted. */
  run: <T>(task: () => Promise<T>) => Promise<GuardedResult<T>>
}

/**
 * Generation counter for async UI (UXR-004..008, P-UI-004). Take a ticket before an await,
 * check it after. Unmounting invalidates, so a late response never touches a gone view.
 */
export function useStaleGuard(): StaleGuard {
  const generation = useRef(0)

  useEffect(
    () => () => {
      generation.current++
    },
    [],
  )

  return useMemo<StaleGuard>(
    () => ({
      ticket: () => generation.current,
      isCurrent: (ticket) => ticket === generation.current,
      invalidate: () => {
        generation.current++
      },
      run: async (task) => {
        const ticket = generation.current
        const value = await task()
        return ticket === generation.current ? { stale: false, value } : { stale: true }
      },
    }),
    [],
  )
}

export interface DialogController<T> {
  isOpen: boolean
  /** Data the dialog was opened with (a loaded record, a row). */
  data: T | undefined
  open: (data?: T) => void
  /** The single close path. Wire it to `<Dialog onClose>`. Invalidates pending loads/saves. */
  close: () => void
  /**
   * Run an async loader, then open with its result, but only if nothing closed, reopened or
   * unmounted the dialog meanwhile. Resolves `true` if it opened. Loader errors propagate.
   */
  openWith: (loader: () => Promise<T>) => Promise<boolean>
  /** The dialog's generation guard: wrap saves in `guard.run()` before closing or toasting. */
  guard: StaleGuard
}

/** Dialog state with the P-UI-004 lifecycle: a stale async response can never reopen it. */
export function useDialog<T = undefined>(): DialogController<T> {
  const [state, setState] = useState<{ isOpen: boolean; data: T | undefined }>({
    isOpen: false,
    data: undefined,
  })
  const guard = useStaleGuard()

  const open = useCallback(
    (data?: T) => {
      guard.invalidate()
      setState({ isOpen: true, data })
    },
    [guard],
  )

  const close = useCallback(() => {
    guard.invalidate()
    setState({ isOpen: false, data: undefined })
  }, [guard])

  const openWith = useCallback(
    async (loader: () => Promise<T>) => {
      guard.invalidate()
      const result = await guard.run(loader)
      if (result.stale) return false
      setState({ isOpen: true, data: result.value })
      return true
    },
    [guard],
  )

  return { ...state, open, close, openWith, guard }
}
